Bacon = require 'baconjs'
Immutable = require 'immutable'
R = require 'ramda'
uuid = require 'uuid'
ordersApi = require 'api/orders'
restaurantsApi = require 'api/restaurant-search'
conditionsUtils = require 'utils/conditionsToQuery'
ChangeTracker = require 'utils/trackSeenOrders'
hash = require 'utils/object-hash'

optimisticUpdates = require 'models/list-optimistic'
import { getSearchAPIVersion } from '../api/api-version'

{ conditionsToQuery } = conditionsUtils

userStore = require 'stores/user'

POLLING_TIME = 3 * 60 * 1000

trackingId = (order) ->
  "#{order.uuid}-#{order.state}"

ordersStreamProducer = (searchParam) ->
  { event, pageInfo, conditions, appliesTo} = searchParam
  sortDir = if pageInfo.sortAsc then "asc" else "desc"
  conditionsObj = conditionsToQuery conditions, userStore.timezone()
  if R.equals(conditionsObj.conditions, [])
    return {results: []}
  else
    Bacon.retry
      source: ->
        console.info "Loading orders from event #{event}"
        Bacon.fromPromise ordersApi.searchRaw appliesTo,
          pageInfo.sort,
          sortDir,
          pageInfo.page,
          conditionsObj
      retries: 5
      isRetryable: (error) ->
        !error
      delay: (context) ->
        5000

countOrders = (searchParams) ->
  { event, pageInfo, conditions, appliesTo} = searchParam
  sortDir = if pageInfo.sortAsc then "asc" else "desc"
  conditionsObj = conditionsToQuery conditions, userStore.timezone()
  ordersApi.countOrders appliesTo, pageInfo.sort, sortDir, pageInfo.page, conditionsObj

CACHE = {}

saveCache = R.curry (key, data) ->
  CACHE[key] = data
  data

loadRestaurantInfo = (restaurantId) ->
  queryData =
      "query GetRestaurantInfo($restaurant_id: Int!) { restaurant(id: $restaurant_id) { id name } }";
  url = "#{RESTAURANTS_SVC_API5}/graphql";
  variables = makeVar restaurantId

  body = R.mergeAll [query: queryData, variables: variables, operationName: "GetRestaurantInfo"]

  key = hash R.pick ['operationName', 'variables'], body

  if CACHE[key]
    return Bacon.once(CACHE[key])
  else
    Bacon.retry
      source: ->
        console.info "Loading restaurants"
        Bacon.fromPromise restaurantsApi.searchGraphql(url, body)
        .map saveCache key
      retries: 5
      isRetryable: (error) ->
        !error
      delay: (context) ->
        5000


makeVar = R.compose R.assoc('restaurant_id', R.__, {}), parseInt

eventsGenerator = (isPolling, manualRefresh, locked) ->
  offset = Math.ceil(Math.random() * 20 - 10)
  seconds = POLLING_TIME + offset
  Bacon.mergeAll [
    if locked then Bacon.later(offset, "Initial loading #{new Date}") else Bacon.never()
    Bacon.interval(seconds).filter(isPolling).flatMapLatest (-> "polling at #{new Date}")
    manualRefresh.map -> "manual refresh #{new Date} (#{seconds})"
  ]

reduceRestaurants =  (sum, r) ->
  R.assoc r.id, r.name, sum

class List
  @MODEL_VERSION: 1
  constructor: (payload) ->
    { @id, @name, @appliesTo, @conditions, @system, @priority, @locked, @version, @soundName, @_version } = payload
    @id ||= uuid()
    @locked = !!@locked
    @version ||= 1
    @_version ||= List.MODEL_VERSION

    @pollingEnabled = new Bacon.Bus
    @isPolling = @pollingEnabled.toProperty(payload.isPolling || false)
    @manualRefresh = new Bacon.Bus
    @active = true
    @sounds = new Bacon.Bus
    @soundName ||= "notification"
    @useSounds = @sounds.toProperty(payload.useSounds || false)


    @_sort = new Bacon.Bus
    @_page = new Bacon.Bus

    @sortProp = @_sort.toProperty {prop: payload.sortProp?.prop || 'time', asc: payload.sortProp?.asc || false}
    @page = @_page.toProperty 1

    @_paginationInfo = Bacon.combineWith ((sortInfo, page) ->
      {sort: sortInfo.prop, sortAsc: sortInfo.asc, page: page}), @sortProp, @page

    @priority ||= 1

    searchParams = Bacon.combineTemplate
      event: eventsGenerator(@isPolling, @manualRefresh, @locked)
      pageInfo: @_paginationInfo
      appliesTo: @appliesTo
      conditions: @conditions

    ordersStreamData = searchParams.flatMapLatest(ordersStreamProducer).toProperty()

    orders = ordersStreamData
      .skipErrors()
      .flatMap R.prop('results')
      .map R.defaultTo([])
      .toProperty([])

 
    @totalOrders = ordersStreamData
      .skipErrors()
      .flatMap R.path([ 'total', 'value' ])
      # .log "Total Orders"
      .toProperty()
    
    @pageNumber = ordersStreamData
      .skipErrors()
      .flatMap R.prop('page')
      .toProperty()
      # .log "Page Number"

    restaurantsStream = orders
      # .log "Orders"
      .map R.map R.prop 'restaurant_id'
      .map R.uniq
      # .log "Restaurant IDs"
      .map R.map loadRestaurantInfo
      .flatMap Bacon.zipAsArray
      .map R.map R.path ['data', 'restaurant']
      .map (rs) -> R.reduce(reduceRestaurants, {}, rs)
      .toProperty()
  
    restaurantsOrdersStream = Bacon.combineTemplate
      orders: orders
      restaurants: restaurantsStream

    ordersStream = restaurantsOrdersStream
      .map ({orders, restaurants}) ->
        orders
          .map (order) ->
            R.assoc 'restaurant_name', restaurants[order.restaurant_id], order
      # .log "Orders Stream"
   
    @orders = Bacon.combineWith ordersStream, optimisticUpdates.orders, optimisticUpdates.combineData


    clearErrors = @orders.map -> null
    errorValues = ordersStreamData
      .mapError (error) -> error
      .filter (error) -> !error.success
    @errors = Bacon.mergeAll(errorValues, clearErrors)

    changeTracker = ordersStreamData
      .scan new ChangeTracker, (t, data) -> t.track data.results.map trackingId
      .skip 2 # Skip first 2 events as they are initial and first read

    @newOrders = changeTracker.map (t) -> t.lastChange > 0
    @makeNoise = changeTracker
      .filter (t) -> t.lastChange > 0
      .filter @useSounds
      .map R.always @soundName

    @changes = @totalOrders
      .skip 1
      .scan Immutable.List([]), (list, total) ->
        list = list.unshift date: new Date, value: total
        list = list.setSize(90) if list.count() > 90
        list

    @loadingOrders = searchParams.awaiting(ordersStreamData)

    # ----- new tab interface
    @type     = 'lists'
    @loading  = @loadingOrders
    @activity = @newOrders
    @noise    = @makeNoise
    @data     = @ordersList
    @totalOrders = @totalOrders
    @pageNumber = @pageNumber

  setPolling: (enabled) ->
    @pollingEnabled.push enabled

  countOrders: ->
    conditions = @conditions
    appliesTo = @appliesTo
    conditionsObj = conditionsToQuery conditions, userStore.timezone()
    ordersApi.countOrders appliesTo, conditionsObj

  setUseSounds: (b) ->
    @sounds.push b

  sortOn: (prop, ascending) ->
    @_sort.push {prop: prop, asc: ascending}

  setPage: (page) ->
    @_page.push page

  refresh: ->
    @manualRefresh.push new Date

  getSearchAPIVersion: () ->
    operator = if @appliesTo == 'all' then 'AND' else 'OR'
    getSearchAPIVersion operator, @conditions

  toExternalForm: ->
    R.pick ['id', 'name', 'type', 'conditions', 'version', '_version', 'appliesTo', 'soundName', 'isPolling', 'useSounds', 'sortProp', 'locked'], @

module.exports = List
